// Shop lights (jobq-api "AFTER-HOURS SECURITY: SHOP LIGHTS", since 2026-11-03). The ONE copy of the calls and wording
// for the Shop lights part of the Doors page and the phone Cameras screen. Each light switch (Main Shop North, Main Shop
// South, Metal Room) has a relay that checks in with JobQ every 30 s. After the shop arms, JobQ waits, blinks the lights
// as a warning and turns them off; flipping the wall switch (or pressing On here) keeps that area on for 2 hours.
// JobQ turns lights ON only when the camera watch sees someone inside while the shop is armed (all three areas, 30
// minutes; since 2026-11-03). The words for where each area stands come from the API (`state_text`).

import { securityFetch } from '../../utils/securityApi';
import { shopClock } from '../../utils/shopDate';

export const LIGHTS_POLL_MS = 30000;
export const LIGHTS_FOLLOW_MS = 5000;   // while a press waits for the relay's next check-in

export const OFF_PRESSED_TEXT = 'Blinking now; off in about a minute unless someone flips the wall switch.';
export const ON_PRESSED_TEXT = 'On sent. Auto-off leaves this area on for 2 hours.';

export const fetchLights = () => securityFetch('/security/lights');

export const sendLightCommand = (relayId, action) =>
  securityFetch(`/security/lights/${relayId}/command`, { method: 'POST', body: JSON.stringify({ action }) });

export const setAutoOff = (relayId, autoOff) =>
  securityFetch(`/security/lights/${relayId}`, { method: 'PATCH', body: JSON.stringify({ auto_off: autoOff }) });

/** True while a press is waiting for a relay to collect it, so the screen reads more often. */
export const anyPending = (data) => ((data && data.relays) || []).some((r) => r.pending === 'on' || r.pending === 'off');

/**
 * What the area's badge says. A failed read or a relay gone quiet outranks the last on/off report: that report may be
 * stale. `readFailed` = JobQ's last answer to this screen was an error.
 */
export function lightStateView(relay, readFailed = false) {
  const last = relay.output === true ? 'on' : relay.output === false ? 'off' : 'unknown';
  if (readFailed) return { label: "CAN'T READ", tone: 'bad', note: `JobQ isn't answering this screen. Last known: ${last}.` };
  if (!relay.reporting) {
    const since = shopClock(relay.last_beat_at);
    return {
      label: 'NOT REPORTING',
      tone: 'bad',
      note: since ? `Not reporting since ${since}. Last known: ${last}.` : "This switch's relay has never reported.",
    };
  }
  if (relay.output === true) return { label: 'LIGHTS ON', tone: 'warn' };
  if (relay.output === false) return { label: 'LIGHTS OFF', tone: 'muted' };
  return { label: 'UNKNOWN', tone: 'muted', note: "The relay hasn't said whether the lights are on." };
}

/** On / Off can't reach a relay that isn't checking in (the API refuses with 409). */
export const lightButtonsDisabled = (relay) => !relay.reporting;

/** One line of health under the area's name, like the locks' battery / signal line. */
export function lightHealthLine(relay) {
  const parts = [];
  if (relay.rssi !== null && relay.rssi !== undefined) parts.push(`Signal ${relay.rssi} dBm`);
  if (relay.temp_c !== null && relay.temp_c !== undefined && Number.isFinite(Number(relay.temp_c))) {
    parts.push(`Relay ${Math.round(Number(relay.temp_c))}°C`);
  }
  if (relay.reporting && relay.last_beat_at) parts.push(`Checked in ${shopClock(relay.last_beat_at)}`);
  return parts.join(' · ');
}

/** A press the relay hasn't collected yet. null when nothing is waiting. */
export function pendingText(relay) {
  if (relay.pending === 'on') return 'On sent: the lights come on at the next check-in (within 30 seconds).';
  if (relay.pending === 'off') return 'Off sent: the lights blink at the next check-in (within 30 seconds), then go off about a minute later.';
  return null;
}

export const autoOffNote = (relay) => (relay.auto_off
  ? 'When the shop arms, JobQ waits 15 minutes (longer while someone is seen or a door opens), blinks these lights twice, and turns them off 5 minutes later. If the cameras see someone inside while the shop is armed, it turns them on for 30 minutes.'
  : 'Auto-off is off: JobQ will not turn these lights off by itself.');

/** The question before Off: the lights go out on whoever is in that area. */
export const offConfirmText = (relay) =>
  `Turn off ${relay.name}? The lights blink now and go off about a minute later, unless someone flips the wall switch.`;

/** What the area shows once a press is accepted. */
export const pressedText = (action) => (action === 'off' ? OFF_PRESSED_TEXT : ON_PRESSED_TEXT);

/** The area's recent history, newest first. With no relayId, every area's (the section-wide list). */
export function lightActivity(data, relayId = null, limit = 8) {
  return ((data && data.events) || [])
    .filter((e) => relayId === null || e.relay_id === relayId)
    .map((e) => ({
      key: `e${e.event_id}`,
      at: e.at,
      text: e.text || `${e.relay_name || 'Lights'}: ${e.kind}`,
      tone: e.kind === 'warn' || e.kind === 'auto_off' ? 'warn' : 'muted',
    }))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    .slice(0, limit)
    .map((x) => ({ ...x, when: x.at ? shopClock(x.at) : '' }));
}
